/**
 * OutsideGeofenceDialog.jsx — shown when a worker tries to Start a job
 * but their GPS is outside the job's geofence (Feb 2026).
 *
 * Standard service-trade practice: GPS is an audit signal, not a hard
 * gate. The worker picks a reason (or "Other" + free text), confirms,
 * and the backend allows the start AND auto-opens a `travel_out`
 * segment recording where they were and why. The work timer still
 * starts immediately — the worker is on the clock with Travel Time
 * logged separately.
 *
 * Network contract:
 *   GET  /api/travel/reasons               → [{code, label}, …]
 *   PUT  /api/jobs/{id}/start              → { latitude, longitude,
 *                                              outside_geofence_reason,
 *                                              outside_geofence_notes? }
 *
 * Usage: caller opens this dialog when the /start call returns 403
 * with the "Travel Time" copy, gathers a reason, then retries /start.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Truck, MapPin, AlertTriangle, Loader2 } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

export default function OutsideGeofenceDialog({
  open,
  onClose,
  // (payload: { reason, notes }) => Promise<void>
  onConfirm,
  distanceMeters = null,
  radiusMeters = null,
  jobTitle = null,
}) {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/travel/reasons`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (!cancelled) setReasons(data?.reasons || []); })
      .catch(() => { if (!cancelled) setReasons([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Reset selection each time the dialog re-opens.
  useEffect(() => { if (open) { setReason(null); setNotes(''); } }, [open]);

  const canConfirm = useMemo(() => {
    if (!reason) return false;
    if (reason === 'other' && !notes.trim()) return false;
    return true;
  }, [reason, notes]);

  const distanceLabel = useMemo(() => {
    if (distanceMeters == null) return null;
    return distanceMeters >= 1000
      ? `${(distanceMeters / 1000).toFixed(1)} km away`
      : `${Math.round(distanceMeters)} m away`;
  }, [distanceMeters]);

  const handleConfirm = async () => {
    if (!canConfirm || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm?.({ reason, notes: notes.trim() || null });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-[95vw] sm:max-w-lg max-h-[92vh] flex flex-col"
        data-testid="outside-geofence-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-amber-500" />
            You&apos;re not at the site yet — start with Travel Time?
          </DialogTitle>
          <DialogDescription>
            {jobTitle ? (
              <>You&apos;re starting <span className="font-semibold">{jobTitle}</span>. </>
            ) : null}
            Your GPS shows you&apos;re{' '}
            {distanceLabel ? <span className="font-semibold">{distanceLabel}</span> : 'outside the job geofence'}
            {radiusMeters ? <> from the site (radius {radiusMeters} m).</> : '.'}
            {' '}
            Pick a reason so your supervisor knows what&apos;s happening — the clock will
            still start, but the time will be logged as billable Travel until you arrive.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading reasons…
            </div>
          ) : reasons.length === 0 ? (
            <div className="flex items-start gap-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-200">
                Couldn&apos;t load reasons. Contact your admin if this persists.
              </p>
            </div>
          ) : (
            reasons.map((r) => (
              <button
                key={r.code}
                type="button"
                onClick={() => setReason(r.code)}
                disabled={submitting}
                className={`w-full text-left flex items-start gap-3 p-3 rounded-md border transition-colors ${
                  reason === r.code
                    ? 'border-amber-500/60 bg-amber-500/10'
                    : 'border-slate-700 hover:border-slate-500'
                }`}
                data-testid={`outside-geofence-reason-${r.code}`}
              >
                <MapPin className={`w-4 h-4 mt-0.5 ${reason === r.code ? 'text-amber-300' : 'text-slate-500'}`} />
                <span className="text-sm leading-relaxed text-slate-200">{r.label}</span>
              </button>
            ))
          )}

          {reason === 'other' && (
            <Textarea
              autoFocus
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Briefly describe why you're starting off-site (required)…"
              className="min-h-[80px] mt-2"
              maxLength={400}
              data-testid="outside-geofence-notes-input"
            />
          )}
          {reason && reason !== 'other' && (
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional extra context for your supervisor…"
              className="min-h-[60px] mt-2"
              maxLength={400}
              data-testid="outside-geofence-notes-optional-input"
            />
          )}
        </div>

        <DialogFooter className="gap-2 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onClose?.()}
            disabled={submitting}
            data-testid="outside-geofence-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm || submitting}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="outside-geofence-confirm-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
            {submitting ? 'Starting…' : 'Start with Travel Time'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
